import { ColumnDef } from '@tanstack/react-table';

/**
 * Restructures cell ID for embedded table rows by injecting array item index
 *
 * @example
 * // Input: cellId = '0_packaging_units.*.length', arrayItemIndex = 1
 * // Output: '0_packaging_units_1_length'
 */
export const buildEmbeddedTableCellId = ({
  cellId,
  arrayItemIndex,
}: {
  cellId: string;
  arrayItemIndex: number;
}): string => {
  const match = cellId.match(/^(\d+)_(.*)/);
  if (!match) {
    return cellId;
  }

  const [, rowIndex, fieldPath] = match;

  return [rowIndex, fieldPath.replace('.*.', `_${arrayItemIndex}_`)].join('_');
};

export const getFieldId = <TData = unknown, TValue = unknown>({
  columnDef,
  dataPrefix,
  cellId,
  parentColumnPath,
}: {
  columnDef: ColumnDef<TData, TValue>;
  dataPrefix: string | undefined;
  cellId: string;
  parentColumnPath?: string;
}): string => {
  const accessorKey = 'accessorKey' in columnDef ? (columnDef.accessorKey as string) : undefined;
  const dotNotationCellId = convertTanstackIdsToDotNotation(cellId);
  const dotNotationAccessorKey = convertTanstackIdsToDotNotation(accessorKey);
  const excludedReplacements = [
    ...(dotNotationAccessorKey?.split('.') || []),
    ...(parentColumnPath ? [parentColumnPath] : []),
  ];

  const prefix = dataPrefix ? `${dataPrefix}.` : '';
  return `${prefix}${cellIdToFieldId({ cellId: dotNotationCellId, excludedReplacements })}`;
};

const cellIdIndexRegex = /_(?=\d)|(?<=\d)_/g;

export const cellIdToFieldId = ({
  cellId,
  excludedReplacements,
}: {
  cellId: string;
  excludedReplacements?: Array<string>;
}): string => {
  let fieldId = cellId.replace(cellIdIndexRegex, '.');
  excludedReplacements?.forEach((replacement) => {
    const dottedReplacement = replacement.replace(cellIdIndexRegex, '.');
    fieldId = fieldId.replace(dottedReplacement, replacement);
  });
  return fieldId;
};

export const convertTanstackIdsToDotNotation = <T extends string | undefined>(id: T): T => {
  return id?.replace(/\[(\d+)\]\./g, '.$1.') as T;
};
